import { useMemo, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { ExternalLink } from "lucide-react"
import { Link } from "react-router-dom"
import { ErrorBox, Loading } from "@/components/portal/LoadingError"
import { toast } from "@/components/portal/Toast"

/**
 * Projektplan der Plattformstrategie „Von Assistenz zu Autonomie" (Paul
 * 24.09.2026: „den projektplan im portal damit wir immer sehen wo wir gerade
 * sind; und pro strategieteil vorher und nachher messungen").
 *
 * Drei Blöcke: die Kennzahlen mit Wochenverlauf und Ziel, die 13 Bausteine mit
 * Stand aus GitHub und Wirkung aus den Schnappschüssen, die Meilensteine.
 * Editierbar sind nur Live-Datum und Notiz je Baustein — der Bearbeitungsstand
 * kommt aus GitHub, die Zahlen aus dem Wochenjob.
 */

type Punkt = { woche_von: string; woche_bis: string; zaehler: number | null; nenner: number | null; wert: number | null }
type Kennzahl = { label: string; einheit: string; richtung: "hoch" | "runter" | null; verlauf: Punkt[]; aktuell: number | null }
type Wirkung = { vorher: number | null; nachher: number | null; wochen_vorher: number; wochen_nachher: number; delta: number | null; urteil: string; live_at: string }
type Issue = { repo: string; nummer: number; url: string; state: string | null; state_reason?: string | null; assignee?: string | null }
export type Baustein = {
  nr: number; titel: string; kanal: string; quartal: string; aufwand_tage: number | null
  stufe_vorher: number | null; stufe_nachher: number | null
  kennzahl: string | null; kennzahl_label: string | null; einheit: string | null; richtung: "hoch" | "runter" | null
  ziel_q4: number | null; ziel_q2: number | null; ziel_je: "monat" | null; ziel_q4_doc: number | null; ziel_q2_doc: number | null
  baseline: number | null; aktuell: number | null
  issue: Issue | null; live_at: string | null; live_at_manuell: boolean
  status: "geplant" | "in_arbeit" | "live" | "gemessen" | "verworfen"
  wirkung: Wirkung | null; notiz: string | null
}
type Meilenstein = { datum: string; titel: string; nachweis: string; bausteine: number[] }
type Antwort = { bausteine: Baustein[]; kennzahlen: Record<string, Kennzahl>; meilensteine: Meilenstein[]; stand: string; tenant: string; wochen_erfasst: number }
type Schatten = { n: number; gleich: number; kaputt?: number; quote: number | null; seit: string | null; modell: string | null; prod_modell: string | null; tage?: number; paare: { prod: string | null; schatten: string | null; n: number }[]; je_tag: { tag: string; n: number; gleich: number }[] }

type Matrix = {
  tage: number; mails: number; mit_gold: number; klassen: string[]
  zellen: Record<string, Record<string, number>>
  je_klasse: Record<string, { gold: number; recall: number | null; praezision: number | null }>
  planer_termin: { n: number; vorgeschlagen: number; quote: number | null }
  teure_fehler: { betreff: string; kategorie: string | null; grund: string; eingang: string }[]
}
const KLASSE_TEXT: Record<string, string> = { spam: "Spam/Werbung", rechnung: "Rechnung", kunde: "Kundenanliegen" }

/**
 * Baustein 3: Klassifikator gegen das, was das Büro mit der Mail getan hat —
 * laufend aus Live-Daten, ohne Modellaufruf. Termin ist hier Teil von
 * „Kundenanliegen"; ob ein Termin erkannt wurde, zeigt die Planer-Zeile.
 */
function MatrixBlock() {
  const q = useQuery<Matrix>({
    queryKey: ["strategie-klassifikator-matrix"],
    queryFn: async () => {
      const r = await fetch("/api/v1/strategie/klassifikator-matrix?tage=28", { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json()
    },
  })
  const m = q.data
  if (!m || !m.zellen || !m.mit_gold) return null
  return (
    <div className="pp-card p-3 mb-6" data-testid="klassifikator-matrix">
      <div className="font-semibold text-[13px] mb-1">Klassifikator gegen Büro-Verhalten (Baustein 3)</div>
      <div className="text-[12px] text-muted-foreground mb-2">
        Letzte {m.tage} Tage: {m.mit_gold} von {m.mails} Mails mit klarem Signal (an Buchhaltung weitergeleitet, beantwortet,
        Aufgabe, Termin, in „00 Werbung“ ohne Aktion). Zeilen = was das Büro getan hat, Spalten = was das Modell sagte.
      </div>
      <table className="text-[12px] mb-2">
        <thead><tr className="text-left text-muted-foreground">
          <th className="pr-3 font-normal">Büro ↓ / Modell →</th>
          {m.klassen.map((k) => <th key={k} className="pr-3 font-normal">{KLASSE_TEXT[k] || k}</th>)}
          <th className="pr-3 font-normal">Erkannt</th><th className="font-normal">Trefferquote</th>
        </tr></thead>
        <tbody>{m.klassen.map((g) => (
          <tr key={g} className="border-t" data-testid={`matrix-${g}`}>
            <td className="pr-3 py-0.5">{KLASSE_TEXT[g] || g}</td>
            {m.klassen.map((p) => <td key={p} className={`pr-3 tabular-nums ${g === p ? "font-semibold" : ""}`}>{m.zellen[g]?.[p] ?? 0}</td>)}
            <td className="pr-3 tabular-nums">{fmt(m.je_klasse[g]?.recall ?? null, "%")}</td>
            <td className="tabular-nums">{fmt(m.je_klasse[g]?.praezision ?? null, "%")}</td>
          </tr>
        ))}</tbody>
      </table>
      <div className="text-[12px] mb-1" data-testid="matrix-planer-termin">
        Termin gebucht: bei <strong>{m.planer_termin.vorgeschlagen} von {m.planer_termin.n}</strong> hatte der Planer ihn schon vorgeschlagen ({fmt(m.planer_termin.quote, "%")}).
      </div>
      {m.teure_fehler.length > 0 && (
        <details className="text-[12px]">
          <summary className="cursor-pointer">Als Spam einsortiert, aber vom Büro bearbeitet ({m.teure_fehler.length})</summary>
          <ul className="mt-1 space-y-0.5">{m.teure_fehler.map((f, i) => (
            <li key={i}>{f.eingang}: {f.betreff || "(ohne Betreff)"} <span className="text-muted-foreground">— Modell: {f.kategorie}, Büro: {f.grund}</span></li>
          ))}</ul>
        </details>
      )}
    </div>
  )
}

function SchattenBlock() {
  const q = useQuery<Schatten>({
    queryKey: ["strategie-schatten"],
    queryFn: async () => {
      const r = await fetch("/api/v1/strategie/schatten?tage=14", { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json()
    },
  })
  const s = q.data
  if (!s || !s.n) return null
  return (
    <div className="pp-card p-3 mb-6" data-testid="schattenlauf">
      <div className="font-semibold text-[13px] mb-1">Schattenlauf Klassifikator (Baustein 14)</div>
      <div className="text-[12px] text-muted-foreground mb-2">
        {s.modell} läuft neben {s.prod_modell} mit, entscheidet nichts. Übereinstimmung der Klasse (Spam, Rechnung, Termin, Kunde) seit {s.seit?.slice(0, 10)}:
        <strong className="text-foreground"> {s.gleich} von {s.n} = {fmt(s.quote, "%")}</strong>{s.kaputt ? `, ${s.kaputt} ohne gültige Antwort` : ""}
      </div>
      {s.paare.length > 0 && (
        <table className="text-[12px]">
          <thead><tr className="text-left text-muted-foreground"><th className="pr-3 font-normal">Prod sagt</th><th className="pr-3 font-normal">Schatten sagt</th><th className="font-normal">Mails</th></tr></thead>
          <tbody>{s.paare.map((p, i) => <tr key={i} className="border-t"><td className="pr-3 py-0.5">{p.prod || "—"}</td><td className="pr-3 py-0.5">{p.schatten || "—"}</td><td className="tabular-nums">{p.n}</td></tr>)}</tbody>
        </table>
      )}
    </div>
  )
}

type ArchivMandant = {
  name: string; modus?: "aus" | "schatten" | "scharf"; faelle: number; widersprueche: number; offen: number; archiviert: number; ampel: Ampel
  ziel?: string | null; ziel_vorbelegt?: string[]; scharf_seit?: string | null; abstieg?: { at: string; grund: string } | null
}
type Ampel = "gruen" | "gelb" | "rot"
type ArchivRegel = {
  regel: string; label: string; beschreibung: string; phase: "vor" | "nach"
  messung_vorher: { mails: number; aktionen: number }
  faelle: number; widersprueche: number; offen: number; archiviert: number; rueckgeholt: number; quote: number | null
  stichproben?: number; stichproben_offen?: number; stichproben_zurueck?: number
  ampel: Ampel; seit: string | null; mandanten: Record<string, ArchivMandant>
  beispiele: { email_id: number; mandant: string; betreff: string; aktionen: string }[]
}
type ArchivStichprobe = { email_id: number; regel: string; regel_label: string; mandant: string; ziel_ordner: string | null; betreff: string; absender: string; entschieden_at: string | null }
type ArchivAntwort = {
  regeln?: ArchivRegel[]; tage: number; reife_stunden: number; freigabe: { min_faelle: number; max_widerspruch: number }; global: string
  abstieg?: { fenster_faelle: number; fenster_tage: number; max_widerspruch: number; rueckholungen: number; rueckholung_tage: number }
  stichprobe_prozent?: number; stichproben?: ArchivStichprobe[]
}

const AMPEL_TEXT: Record<Ampel, string> = { gruen: "Freigabereif", gelb: "Sammelt", rot: "Zu viele Widersprüche" }
const AMPEL_KLASSE: Record<Ampel, string> = {
  gruen: "bg-emerald-100 text-emerald-900", gelb: "bg-amber-100 text-amber-900", rot: "bg-red-100 text-red-900",
}
const MODUS_TEXT = { aus: "Aus", schatten: "Schatten", scharf: "Scharf" } as const

async function sende(url: string, method: string, body: unknown) {
  const r = await fetch(url, {
    method, credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
  })
  if (!r.ok) {
    const d = await r.json().catch(() => ({}))
    throw new Error(d?.detail || `HTTP ${r.status}`)
  }
  return r.json()
}

function datumKurz(iso?: string | null): string {
  if (!iso) return ""
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })
}

/**
 * Zielordner nur zur Ansicht — gepflegt wird er in der Tenant-Konfiguration
 * (Paul 27.09.2026), eine Quelle für alle Regeln einer Art.
 */
function ZielOrdner({ slug, m }: { slug: string; m: ArchivMandant }) {
  const anzeige = m.ziel || `${(m.ziel_vorbelegt || []).join(" / ")} (vorbelegt)`
  return (
    <span className="text-[11px] text-muted-foreground" data-testid={`ziel-${slug}`}>
      → {anzeige} · <Link to={`/tenants/${encodeURIComponent(slug)}`} className="underline">ändern in der Tenant-Konfiguration</Link>
    </span>
  )
}

/**
 * Stichprobe (Stufe 4): ein fester Anteil der scharf archivierten Mails
 * landet hier. „Passt“ zählt als geprüft, „Zurückholen“ legt die Mail zurück
 * in den Posteingang und zählt als Widerspruch — zwei davon in sieben Tagen
 * stellen die Regel automatisch zurück auf Schatten.
 */
function StichprobenListe({ liste, prozent }: { liste: ArchivStichprobe[]; prozent: number }) {
  const qc = useQueryClient()
  const pruefen = useMutation({
    mutationFn: (b: { email_id: number; ergebnis: "bestaetigt" | "zurueckholen" }) =>
      sende(`/api/v1/strategie/archiv-stichprobe/${b.email_id}`, "POST", { ergebnis: b.ergebnis }),
    onSuccess: (d: { ergebnis?: string; zurueckgestuft?: boolean }) => {
      toast(d?.ergebnis === "zurueckgeholt"
        ? (d.zurueckgestuft ? "Zurück im Posteingang – Regel steht wieder auf Schatten" : "Zurück im Posteingang")
        : "Als geprüft vermerkt")
      qc.invalidateQueries({ queryKey: ["strategie-archiv"] })
    },
    onError: (e) => toast(`Nicht gespeichert: ${e instanceof Error ? e.message : String(e)}`, "error"),
  })
  if (!liste.length) return null
  return (
    <div className="mb-3 rounded border border-amber-300 bg-amber-50 p-2" data-testid="archiv-stichproben">
      <div className="text-[12.5px] font-medium mb-1">
        Automatisch archiviert – bitte kurz prüfen ({liste.length})
        <span className="font-normal text-muted-foreground"> · Stichprobe {fmt(prozent, "%")} der scharf archivierten Mails</span>
      </div>
      <ul className="text-[12px]">
        {liste.map((s) => (
          <li key={s.email_id} className="flex flex-wrap items-baseline gap-x-2 border-t border-amber-200 py-1 first:border-t-0">
            <span className="font-medium">{s.betreff || "(ohne Betreff)"}</span>
            <span className="text-muted-foreground">{s.absender}</span>
            <span className="text-muted-foreground">· {s.mandant} · {s.regel_label} → {s.ziel_ordner}{s.entschieden_at ? ` · ${datumKurz(s.entschieden_at)}` : ""}</span>
            <span className="ml-auto flex gap-2">
              <button type="button" className="underline" disabled={pruefen.isPending}
                      onClick={() => pruefen.mutate({ email_id: s.email_id, ergebnis: "bestaetigt" })}>Passt</button>
              <button type="button" className="underline text-red-800" disabled={pruefen.isPending}
                      onClick={() => pruefen.mutate({ email_id: s.email_id, ergebnis: "zurueckholen" })}>Zurückholen</button>
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * Warum „Scharf“ für diesen Betrieb noch gesperrt ist. Die Ampel der Regel
 * oben zählt alle Betriebe zusammen, freigegeben wird aber je Betrieb — ohne
 * diesen Hinweis sieht „Freigabereif“ neben einem gesperrten Schalter wie ein
 * Fehler aus. Auf „Aus“ zählt die Regel nichts mehr dazu.
 */
function ScharfHinweis({ m, freigabe }: {
  m: { modus?: "aus" | "schatten" | "scharf"; faelle: number; ampel: Ampel }
  freigabe: { min_faelle: number; max_widerspruch: number }
}) {
  if (m.modus === "scharf" || m.ampel === "gruen") return null
  const fehlen = Math.max(0, freigabe.min_faelle - m.faelle)
  const grund = m.ampel === "rot"
    ? `zu viele Widersprüche (über ${fmt(freigabe.max_widerspruch, "%")})`
    : `noch ${fehlen} von ${freigabe.min_faelle} Fällen`
  return (
    <div className="pl-1 text-[11px] text-muted-foreground" data-testid="scharf-hinweis">
      Scharf gesperrt: {grund}{m.modus === "aus" ? " · auf „Aus“ wird nicht gezählt, erst Schatten einschalten" : ""}
    </div>
  )
}

/**
 * Archiv-Automatik (Baustein 1, #120): je Regel, wie oft sie entschieden hätte
 * und wie oft das Büro danach doch etwas getan hat. „Scharf“ lässt der Server
 * erst bei grüner Ampel des Mandanten zu (≥ 100 Fälle, ≤ 1 % Widerspruch).
 */
function ArchivBlock() {
  const qc = useQueryClient()
  const q = useQuery<ArchivAntwort>({
    queryKey: ["strategie-archiv"],
    queryFn: async () => {
      const r = await fetch("/api/v1/strategie/archiv-schatten?tage=60", { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json()
    },
  })
  const schalten = useMutation({
    mutationFn: (body: { tenant: string; regel: string; modus: string }) => sende("/api/v1/strategie/archiv-regeln", "PATCH", body),
    onSuccess: () => { toast("Schalter gespeichert"); qc.invalidateQueries({ queryKey: ["strategie-archiv"] }) },
    onError: (e) => toast(`Nicht gespeichert: ${e instanceof Error ? e.message : String(e)}`, "error"),
  })
  const a = q.data
  if (!a || !Array.isArray(a.regeln)) return null
  return (
    <div className="pp-card p-3 mb-6 overflow-x-auto" data-testid="archiv-automatik">
      <div className="font-semibold text-[13px] mb-1">Archiv-Automatik (Baustein 1)</div>
      <div className="text-[12px] text-muted-foreground mb-2 max-w-[90ch]">
        Feste Regeln ohne KI. Im Schatten entscheidet die Regel nur und verschiebt nichts; gezählt wird, ob das Büro danach
        doch etwas getan hat (Aufgabe, Antwort, Weiterleitung, HERO-Verknüpfung, Markierung, Plan ausgeführt) oder eine
        archivierte Mail zurückholt. Ein Fall zählt nach {a.reife_stunden} Stunden. Freigabe für „Scharf“ ab{" "}
        {a.freigabe.min_faelle} Fällen mit höchstens {fmt(a.freigabe.max_widerspruch, "%")} Widerspruch.
        {a.abstieg && <> Eine scharfe Regel geht von selbst zurück auf Schatten, wenn auf den letzten {a.abstieg.fenster_faelle} Fällen
          ({a.abstieg.fenster_tage} Tage) mehr als {fmt(a.abstieg.max_widerspruch, "%")} widersprochen wird oder {a.abstieg.rueckholungen} Mails
          in {a.abstieg.rueckholung_tage} Tagen zurückgeholt werden; wieder scharf nur von Hand.</>}
        {a.global !== "an" && <strong className="text-foreground"> Global per .env: {a.global}.</strong>}
      </div>
      <StichprobenListe liste={a.stichproben || []} prozent={a.stichprobe_prozent ?? 5} />
      <table className="w-full text-[12.5px]">
        <thead>
          <tr className="text-left text-muted-foreground">
            <th className="pb-1 pr-3 font-normal">Regel</th>
            <th className="pb-1 pr-3 font-normal">Vorher (30 T)</th>
            <th className="pb-1 pr-3 font-normal">Fälle</th>
            <th className="pb-1 pr-3 font-normal">Widerspruch</th>
            <th className="pb-1 pr-3 font-normal">Freigabe</th>
            <th className="pb-1 font-normal">Schalter je Betrieb</th>
          </tr>
        </thead>
        <tbody>
          {a.regeln.map((r) => (
            <tr key={r.regel} className="border-t align-top" data-testid={`archiv-${r.regel}`}>
              <td className="py-1.5 pr-3">
                <div className="font-medium">{r.label}</div>
                <div className="text-[11px] text-muted-foreground max-w-[42ch]">{r.beschreibung}</div>
                {r.beispiele.length > 0 && (
                  <details className="text-[11px] mt-0.5">
                    <summary className="cursor-pointer text-muted-foreground">Widersprüche ansehen</summary>
                    <ul className="list-disc pl-4">
                      {r.beispiele.map((b) => <li key={b.email_id}>{b.betreff || "(ohne Betreff)"} — {b.aktionen}</li>)}
                    </ul>
                  </details>
                )}
              </td>
              <td className="py-1.5 pr-3 tabular-nums text-muted-foreground">{r.messung_vorher.mails} Mails, {r.messung_vorher.aktionen} Aktionen</td>
              <td className="py-1.5 pr-3 tabular-nums">{r.faelle}{r.offen ? <span className="text-muted-foreground"> (+{r.offen} offen)</span> : null}</td>
              <td className="py-1.5 pr-3 tabular-nums">
                {r.widersprueche}{r.quote != null ? ` = ${fmt(r.quote, "%")}` : ""}{r.rueckgeholt ? `, ${r.rueckgeholt} zurückgeholt` : ""}
                {r.stichproben ? <div className="text-[11px] text-muted-foreground">Stichprobe {r.stichproben - (r.stichproben_offen || 0)}/{r.stichproben} geprüft{r.stichproben_zurueck ? `, ${r.stichproben_zurueck} zurück` : ""}</div> : null}
              </td>
              <td className="py-1.5 pr-3">
                <span className={`inline-block rounded px-1.5 py-0.5 text-[11.5px] ${AMPEL_KLASSE[r.ampel]}`}>{AMPEL_TEXT[r.ampel]}</span>
                {r.archiviert > 0 && <div className="text-[11px] text-muted-foreground">{r.archiviert} archiviert</div>}
              </td>
              <td className="py-1.5">
                {Object.entries(r.mandanten).filter(([, m]) => m.modus).map(([slug, m]) => (
                  <div key={slug} className="mb-1">
                  <label className="flex items-center gap-1.5 text-[11.5px]">
                    <select
                      className="pp-input pp-schalter" value={m.modus}
                      aria-label={`${r.label} bei ${m.name}`}
                      onChange={(e) => schalten.mutate({ tenant: slug, regel: r.regel, modus: e.target.value })}
                    >
                      {(["aus", "schatten", "scharf"] as const).map((mo) => (
                        <option key={mo} value={mo} disabled={mo === "scharf" && m.ampel !== "gruen" && m.modus !== "scharf"}>
                          {MODUS_TEXT[mo]}{mo === "scharf" && m.ampel !== "gruen" && m.modus !== "scharf" ? " (gesperrt)" : ""}
                        </option>
                      ))}
                    </select>
                    <span>{m.name}</span>
                    <span className="text-muted-foreground tabular-nums">{m.faelle}/{m.widersprueche}</span>
                  </label>
                  <ScharfHinweis m={m} freigabe={a.freigabe} />
                  <div className="pl-1"><ZielOrdner slug={slug} m={m} /></div>
                  {m.abstieg && (
                    <div className="pl-1 text-[11px] text-red-800" data-testid={`abstieg-${r.regel}-${slug}`}>
                      Automatisch zurückgestuft {datumKurz(m.abstieg.at)}: {m.abstieg.grund}
                    </div>
                  )}
                  </div>
                ))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

type GesehenMandant = {
  name: string; modus?: "aus" | "schatten" | "scharf"; faelle: number; widersprueche: number; uebereinstimmung: number
  offen: number; gesetzt: number; ampel: Ampel; abstieg?: { at: string; grund: string } | null
}
type GesehenRegel = {
  regel: string; label: string; beschreibung: string; messung_vorher: { mails: number; aktionen: number }
  faelle: number; widersprueche: number; uebereinstimmung: number; offen: number; gesetzt: number
  quote: number | null; uebereinstimmung_quote: number | null; ampel: Ampel; seit: string | null
  mandanten: Record<string, GesehenMandant>; beispiele: { email_id: number; mandant: string; betreff: string; aktionen: string }[]
}
type GesehenAntwort = { regeln?: GesehenRegel[]; reife_stunden: number; freigabe: { min_faelle: number; max_widerspruch: number }; global: string }

/**
 * Auto-gesehen (Baustein 15, 29.09.2026): Werbung ohne Klick auf „gesehen“.
 * Im Schatten ändert sich nichts; gezählt wird, ob das Büro die Mail selbst
 * weggeklickt hat (Übereinstimmung) oder doch etwas getan hat (Widerspruch).
 * Phishing ist bewusst nicht dabei.
 */
export function GesehenBlock() {
  const qc = useQueryClient()
  const q = useQuery<GesehenAntwort>({
    queryKey: ["strategie-gesehen"],
    queryFn: async () => {
      const r = await fetch("/api/v1/strategie/auto-gesehen?tage=60", { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json()
    },
  })
  const schalten = useMutation({
    mutationFn: (body: { tenant: string; regel: string; modus: string }) => sende("/api/v1/strategie/auto-gesehen-regeln", "PATCH", body),
    onSuccess: () => { toast("Schalter gespeichert"); qc.invalidateQueries({ queryKey: ["strategie-gesehen"] }) },
    onError: (e) => toast(`Nicht gespeichert: ${e instanceof Error ? e.message : String(e)}`, "error"),
  })
  const a = q.data
  if (!a || !Array.isArray(a.regeln)) return null
  return (
    <div className="pp-card p-3 mb-6 overflow-x-auto" data-testid="auto-gesehen">
      <div className="font-semibold text-[13px] mb-1">Werbung automatisch „gesehen“ (Baustein 15)</div>
      <div className="text-[12px] text-muted-foreground mb-2 max-w-[90ch]">
        Die Mail bleibt im Posteingang, nur der Klick auf „gesehen“ entfällt. Im Schatten ändert sich nichts; gezählt wird,
        ob das Büro die Mail selbst weggeklickt hat (Übereinstimmung) oder doch etwas getan hat (Widerspruch). Phishing ist
        nicht dabei – dort lagen echte Rechnungen. Ein Fall zählt nach {a.reife_stunden} Stunden. Freigabe ab{" "}
        {a.freigabe.min_faelle} Fällen mit höchstens {fmt(a.freigabe.max_widerspruch, "%")} Widerspruch; ein Widerspruch im
        Scharf-Betrieb stellt die Regel von selbst zurück auf Schatten.
        {a.global !== "an" && <strong className="text-foreground"> Global per .env: {a.global}.</strong>}
      </div>
      <table className="w-full text-[12.5px]">
        <thead>
          <tr className="text-left text-muted-foreground">
            <th className="pb-1 pr-3 font-normal">Regel</th>
            <th className="pb-1 pr-3 font-normal">Vorher (30 T)</th>
            <th className="pb-1 pr-3 font-normal">Fälle</th>
            <th className="pb-1 pr-3 font-normal">Büro gleich</th>
            <th className="pb-1 pr-3 font-normal">Widerspruch</th>
            <th className="pb-1 pr-3 font-normal">Freigabe</th>
            <th className="pb-1 font-normal">Schalter je Betrieb</th>
          </tr>
        </thead>
        <tbody>
          {a.regeln.map((r) => (
            <tr key={r.regel} className="border-t align-top" data-testid={`gesehen-${r.regel}`}>
              <td className="py-1.5 pr-3">
                <div className="font-medium">{r.label}</div>
                <div className="text-[11px] text-muted-foreground max-w-[42ch]">{r.beschreibung}</div>
                {r.beispiele.length > 0 && (
                  <details className="text-[11px] mt-0.5">
                    <summary className="cursor-pointer text-muted-foreground">Widersprüche ansehen</summary>
                    <ul className="list-disc pl-4">
                      {r.beispiele.map((b) => <li key={b.email_id}>{b.betreff || "(ohne Betreff)"} — {b.aktionen}</li>)}
                    </ul>
                  </details>
                )}
              </td>
              <td className="py-1.5 pr-3 tabular-nums text-muted-foreground">{r.messung_vorher.mails} Mails, {r.messung_vorher.aktionen} Aktionen</td>
              <td className="py-1.5 pr-3 tabular-nums">{r.faelle}{r.offen ? <span className="text-muted-foreground"> (+{r.offen} offen)</span> : null}</td>
              <td className="py-1.5 pr-3 tabular-nums">{r.uebereinstimmung}{r.uebereinstimmung_quote != null ? ` = ${fmt(r.uebereinstimmung_quote, "%")}` : ""}</td>
              <td className="py-1.5 pr-3 tabular-nums">{r.widersprueche}{r.quote != null ? ` = ${fmt(r.quote, "%")}` : ""}</td>
              <td className="py-1.5 pr-3">
                <span className={`inline-block rounded px-1.5 py-0.5 text-[11.5px] ${AMPEL_KLASSE[r.ampel]}`}>{AMPEL_TEXT[r.ampel]}</span>
                {r.gesetzt > 0 && <div className="text-[11px] text-muted-foreground">{r.gesetzt} automatisch gesehen</div>}
              </td>
              <td className="py-1.5">
                {Object.entries(r.mandanten).filter(([, m]) => m.modus).map(([slug, m]) => (
                  <div key={slug} className="mb-1">
                    <label className="flex items-center gap-1.5 text-[11.5px]">
                      <select
                        className="pp-input pp-schalter" value={m.modus}
                        aria-label={`${r.label} bei ${m.name}`}
                        onChange={(e) => schalten.mutate({ tenant: slug, regel: r.regel, modus: e.target.value })}
                      >
                        {(["aus", "schatten", "scharf"] as const).map((mo) => (
                          <option key={mo} value={mo} disabled={mo === "scharf" && m.ampel !== "gruen" && m.modus !== "scharf"}>
                          {MODUS_TEXT[mo]}{mo === "scharf" && m.ampel !== "gruen" && m.modus !== "scharf" ? " (gesperrt)" : ""}
                        </option>
                        ))}
                      </select>
                      <span>{m.name}</span>
                      <span className="text-muted-foreground tabular-nums">{m.faelle}/{m.widersprueche}</span>
                    </label>
                    <ScharfHinweis m={m} freigabe={a.freigabe} />
                    {m.abstieg && (
                      <div className="pl-1 text-[11px] text-red-800" data-testid={`gesehen-abstieg-${r.regel}-${slug}`}>
                        Automatisch zurückgestuft {datumKurz(m.abstieg.at)}: {m.abstieg.grund}
                      </div>
                    )}
                  </div>
                ))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const STATUS_TEXT: Record<Baustein["status"], string> = {
  geplant: "Geplant", in_arbeit: "In Arbeit", live: "Live, Messung läuft", gemessen: "Gemessen", verworfen: "Verworfen",
}
const STATUS_KLASSE: Record<Baustein["status"], string> = {
  geplant: "bg-muted text-muted-foreground", in_arbeit: "bg-amber-100 text-amber-900",
  live: "bg-sky-100 text-sky-900", gemessen: "bg-emerald-100 text-emerald-900", verworfen: "bg-muted text-muted-foreground line-through",
}
const URTEIL_TEXT: Record<string, string> = {
  besser: "Wirkt", schlechter: "Schlechter", unveraendert: "Keine Bewegung", zu_frueh: "Zu früh (unter 2 Wochen)", keine_kennzahl: "Keine Kennzahl",
}
const KANAL_TEXT: Record<string, string> = { mail: "E-Mail", telefon: "Telefon", portal: "Portal", whatsapp: "WhatsApp" }

// Die Kennzahlen, die oben als Karten stehen — die Kopfzahlen der Strategie.
const KARTEN = ["stp_quote", "nutzquote", "trefferquote", "bot_containment", "bot_nacharbeit_vermeidbar", "outbound_termin_anrufe", "aufgaben_chef", "buero_portalminuten", "llm_aufrufe_je_mail"]

export function fmt(wert: number | null | undefined, einheit?: string | null): string {
  if (wert == null) return "—"
  const z = Number.isInteger(wert) ? String(wert) : wert.toFixed(1).replace(".", ",")
  return einheit === "%" ? `${z} %` : z
}

export function Sparkline({ punkte, richtung }: { punkte: Punkt[]; richtung: "hoch" | "runter" | null }) {
  const werte = punkte.map((p) => p.wert).filter((w): w is number => w != null)
  if (werte.length < 2) return <span className="text-[11px] text-muted-foreground">noch kein Verlauf</span>
  const min = Math.min(...werte), max = Math.max(...werte)
  const w = 120, h = 28
  const pts = werte.map((v, i) => {
    const x = (i / (werte.length - 1)) * (w - 2) + 1
    const y = max === min ? h / 2 : h - 1 - ((v - min) / (max - min)) * (h - 2)
    return `${x.toFixed(1)},${y.toFixed(1)}`
  })
  const letzte = werte[werte.length - 1], erste = werte[0]
  const besser = richtung == null ? null : richtung === "hoch" ? letzte >= erste : letzte <= erste
  const farbe = besser == null ? "currentColor" : besser ? "#059669" : "#dc2626"
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-label={`Verlauf ${werte.length} Wochen`} className="text-muted-foreground">
      <polyline fill="none" stroke={farbe} strokeWidth="1.5" points={pts.join(" ")} />
    </svg>
  )
}

function KennzahlKarte({ schluessel, k, ziel }: { schluessel: string; k: Kennzahl; ziel: { q4: number | null; q2: number | null; je: string | null } }) {
  return (
    <div className="pp-card p-3 flex flex-col gap-1" data-testid={`kpi-${schluessel}`}>
      <div className="text-[12px] text-muted-foreground leading-tight">{k.label}</div>
      <div className="flex items-end justify-between gap-2">
        <span className="text-[22px] font-semibold tabular-nums">{fmt(k.aktuell, k.einheit)}</span>
        <Sparkline punkte={k.verlauf} richtung={k.richtung} />
      </div>
      <div className="text-[11px] text-muted-foreground">
        Ziel Q4: {fmt(ziel.q4, k.einheit)} · Q2/27: {fmt(ziel.q2, k.einheit)}{ziel.je === "monat" ? " (je Woche, aus Monatsziel)" : ""}
        {k.einheit !== "%" && k.verlauf.length ? " · je Woche" : ""}
      </div>
    </div>
  )
}

function BausteinZeile({ b, speichern }: { b: Baustein; speichern: (nr: number, body: Record<string, unknown>) => void }) {
  const [offen, setOffen] = useState(false)
  const [live, setLive] = useState(b.live_at ? b.live_at.slice(0, 10) : "")
  const [notiz, setNotiz] = useState(b.notiz || "")
  const w = b.wirkung
  return (
    <>
      <tr className="border-t align-top" data-testid={`baustein-${b.nr}`}>
        <td className="py-2 pr-2 tabular-nums text-muted-foreground">{b.nr}</td>
        <td className="py-2 pr-3">
          <div className="font-medium text-[13px]">{b.titel}</div>
          <div className="text-[11.5px] text-muted-foreground">
            {KANAL_TEXT[b.kanal] || b.kanal} · {b.quartal}{b.aufwand_tage ? ` · ${b.aufwand_tage} Tage` : ""}
            {b.stufe_vorher != null && b.stufe_nachher != null ? ` · Stufe ${b.stufe_vorher} → ${b.stufe_nachher}` : ""}
          </div>
        </td>
        <td className="py-2 pr-3">
          <span className={`inline-block rounded px-1.5 py-0.5 text-[11.5px] ${STATUS_KLASSE[b.status]}`}>{STATUS_TEXT[b.status]}</span>
          {b.issue && (
            <a href={b.issue.url} target="_blank" rel="noreferrer" className="ml-2 inline-flex items-center gap-0.5 text-[11.5px] underline text-muted-foreground">
              #{b.issue.nummer}<ExternalLink className="w-3 h-3" />
            </a>
          )}
          {b.live_at && <div className="text-[11px] text-muted-foreground">live seit {b.live_at.slice(0, 10)}{b.live_at_manuell ? " (von Hand)" : ""}</div>}
        </td>
        <td className="py-2 pr-3 text-[12.5px]">
          {b.kennzahl ? (
            <>
              <div>{b.kennzahl_label}</div>
              <div className="tabular-nums text-muted-foreground">
                {fmt(b.baseline, b.einheit)} → <strong className="text-foreground">{fmt(b.aktuell, b.einheit)}</strong> · Ziel {fmt(b.ziel_q4 ?? b.ziel_q2, b.einheit)}
                {b.ziel_je === "monat" ? " je Woche" : ""}
              </div>
            </>
          ) : <span className="text-muted-foreground">keine Kennzahl</span>}
        </td>
        <td className="py-2 pr-3 text-[12.5px]">
          {w ? (
            <>
              <div className="tabular-nums">{fmt(w.vorher, b.einheit)} → {fmt(w.nachher, b.einheit)}{w.delta != null ? ` (${w.delta > 0 ? "+" : ""}${fmt(w.delta, b.einheit)})` : ""}</div>
              <div className="text-[11px] text-muted-foreground">{URTEIL_TEXT[w.urteil] || w.urteil} · {w.wochen_vorher} Wochen vorher, {w.wochen_nachher} nachher</div>
            </>
          ) : <span className="text-muted-foreground">{b.live_at ? "wird gerechnet" : "erst nach Live-Datum"}</span>}
        </td>
        <td className="py-2 text-right">
          <button type="button" className="pp-btn pp-btn-sm" onClick={() => setOffen(!offen)}>{offen ? "Schließen" : "Bearbeiten"}</button>
        </td>
      </tr>
      {offen && (
        <tr className="bg-muted/30" data-testid={`baustein-${b.nr}-form`}>
          <td />
          <td colSpan={5} className="py-2 pr-3">
            <div className="flex flex-wrap items-end gap-3 text-[12.5px]">
              <label className="flex flex-col gap-1">
                <span className="text-muted-foreground">Live-Datum (leer = aus GitHub)</span>
                <input type="date" className="pp-input" value={live} onChange={(e) => setLive(e.target.value)} />
              </label>
              <label className="flex flex-col gap-1 grow min-w-[240px]">
                <span className="text-muted-foreground">Notiz</span>
                <input type="text" className="pp-input" value={notiz} onChange={(e) => setNotiz(e.target.value)} placeholder="z. B. auf Prod seit …, Besonderheit der Messung" />
              </label>
              <button
                type="button" className="pp-btn pp-btn-sm"
                onClick={() => { speichern(b.nr, live ? { live_at: `${live}T12:00:00+02:00`, notiz } : { live_at_loeschen: true, notiz }); setOffen(false) }}
              >Speichern</button>
            </div>
            {b.notiz && !offen ? null : null}
          </td>
        </tr>
      )}
    </>
  )
}

export default function ProjektplanPage() {
  const qc = useQueryClient()
  const daten = useQuery<Antwort>({
    queryKey: ["strategie"],
    queryFn: async () => {
      const r = await fetch("/api/v1/strategie", { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json()
    },
  })
  const aendern = useMutation({
    mutationFn: async ({ nr, body }: { nr: number; body: Record<string, unknown> }) => {
      const r = await fetch(`/api/v1/strategie/bausteine/${nr}`, {
        method: "PATCH", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
      })
      if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json()
    },
    onSuccess: () => { toast("Baustein gespeichert"); qc.invalidateQueries({ queryKey: ["strategie"] }) },
    onError: (e) => toast(`Speichern fehlgeschlagen: ${String(e)}`, "error"),
  })

  const ziele = useMemo(() => {
    const m: Record<string, { q4: number | null; q2: number | null; je: string | null }> = {}
    for (const b of daten.data?.bausteine || []) {
      if (b.kennzahl && !m[b.kennzahl]) m[b.kennzahl] = { q4: b.ziel_q4, q2: b.ziel_q2, je: b.ziel_je }
    }
    return m
  }, [daten.data])

  if (daten.isLoading) return <div className="px-10 py-6"><Loading /></div>
  if (daten.error) return <div className="px-10 py-6"><ErrorBox error={daten.error} /></div>
  const d = daten.data!
  const bausteine = d.bausteine || []
  const live = bausteine.filter((b) => b.status === "live" || b.status === "gemessen").length
  const inArbeit = bausteine.filter((b) => b.status === "in_arbeit").length
  const statusVon = (nr: number) => bausteine.find((b) => b.nr === nr)?.status

  return (
    <div className="px-10 pb-8 pt-4 max-w-[1200px]" data-testid="projektplan">
      <p className="text-[13.5px] text-muted-foreground mb-4 max-w-[80ch]">
        Wo die Strategie steht, in Zahlen. Der Bearbeitungsstand kommt aus den GitHub-Issues, die Kennzahlen aus
        einem Wochen-Schnappschuss ohne Personendaten, die Wirkung je Baustein aus dem Vergleich der vier Wochen vor dem
        Live-Datum mit den Wochen danach. Erfasst: {d.wochen_erfasst} Wochen. Stand {d.stand}.
      </p>

      <div className="text-[12.5px] mb-2">
        <strong>{live}</strong> von {bausteine.length} Bausteinen live, <strong>{inArbeit}</strong> in Arbeit.
      </div>

      <div className="grid md:grid-cols-4 gap-3 mb-6" data-testid="kpi-karten">
        {KARTEN.filter((k) => d.kennzahlen?.[k]).map((k) => (
          <KennzahlKarte key={k} schluessel={k} k={d.kennzahlen[k]} ziel={ziele[k] || { q4: null, q2: null, je: null }} />
        ))}
      </div>

      <MatrixBlock />

      <SchattenBlock />

      <ArchivBlock />

      <GesehenBlock />

      <h2 className="text-[14px] font-semibold mb-1">Bausteine</h2>
      <p className="text-[12px] text-muted-foreground mb-2">
        Baseline = Mittel der letzten vier erfassten Wochen (vor dem Live-Datum: der vier Wochen davor). Ein Urteil gibt es erst ab zwei abgeschlossenen Wochen nach dem Live-Datum.
      </p>
      <div className="pp-card p-3 mb-6 overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="text-left text-muted-foreground">
              <th className="pb-1 pr-2 font-normal">Nr.</th>
              <th className="pb-1 pr-3 font-normal">Baustein</th>
              <th className="pb-1 pr-3 font-normal">Stand</th>
              <th className="pb-1 pr-3 font-normal">Kennzahl: Baseline → aktuell · Ziel</th>
              <th className="pb-1 pr-3 font-normal">Wirkung vorher → nachher</th>
              <th className="pb-1 font-normal" />
            </tr>
          </thead>
          <tbody>
            {bausteine.map((b) => <BausteinZeile key={b.nr} b={b} speichern={(nr, body) => aendern.mutate({ nr, body })} />)}
          </tbody>
        </table>
      </div>

      <h2 className="text-[14px] font-semibold mb-2">Meilensteine</h2>
      <div className="pp-card p-3 mb-6">
        <table className="w-full text-[12.5px]">
          <thead><tr className="text-left text-muted-foreground"><th className="pb-1 pr-3 font-normal">Datum</th><th className="pb-1 pr-3 font-normal">Meilenstein</th><th className="pb-1 pr-3 font-normal">Nachweis</th><th className="pb-1 font-normal">Bausteine</th></tr></thead>
          <tbody>
            {(d.meilensteine || []).map((m) => (
              <tr key={m.datum} className="border-t" data-testid={`meilenstein-${m.datum}`}>
                <td className="py-1.5 pr-3 tabular-nums">{m.datum}</td>
                <td className="py-1.5 pr-3">{m.titel}</td>
                <td className="py-1.5 pr-3 text-muted-foreground">{m.nachweis}</td>
                <td className="py-1.5">
                  {m.bausteine.map((nr) => {
                    const s = statusVon(nr)
                    return <span key={nr} className={`inline-block rounded px-1.5 py-0.5 mr-1 text-[11px] ${s ? STATUS_KLASSE[s] : "bg-muted"}`} title={s ? STATUS_TEXT[s] : ""}>{nr}</span>
                  })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <details className="text-[12px] text-muted-foreground">
        <summary className="cursor-pointer">Alle Kennzahlen mit Wochenverlauf</summary>
        <table className="w-full mt-2">
          <tbody>
            {Object.entries(d.kennzahlen || {}).map(([k, v]) => (
              <tr key={k} className="border-t" data-testid={`kz-${k}`}>
                <td className="py-1 pr-3">{v.label}</td>
                <td className="py-1 pr-3 tabular-nums text-foreground">{fmt(v.aktuell, v.einheit)}</td>
                <td className="py-1 pr-3"><Sparkline punkte={v.verlauf} richtung={v.richtung} /></td>
                <td className="py-1 tabular-nums">{v.verlauf.map((p) => fmt(p.wert, v.einheit)).join(" · ")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </div>
  )
}
